'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetUserPnlChart } from '@morfi/backend-sdk-v2/client';
import { useLocale, useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { PnlChartCardLiveline } from '@/components/portfolio/cards/pnl-chart-card-liveline';
import { buildPnlChartPresentation, type CachedSeries } from '@/components/portfolio/cards/pnl-chart-series';
import { availablePnlRanges, type PnlRange } from '@/components/portfolio/cards/pnl-chart-window';
import { useRefetchWhenVisible } from '@/components/portfolio/cards/use-refetch-when-visible';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/app-tabs';
import { Panel, PanelContent, PanelHeader } from '@/components/ui/panel';
import { useFormattedCurrency } from '@/lib/currency';
import { logger } from '@/lib/telemetry/logger';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PnlChartCardProps {
  /** Wallet pubkey or user identifier accepted by the chart endpoint. */
  pubkey: string;
  registeredAt: string | null;
  /** Hide the in-card title (when the surrounding modal already supplies one). */
  hideTitle?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RANGE_TAB_LABELS: Record<PnlRange, string> = {
  '1d': '1D',
  '1w': '1W',
  '1m': '1M',
  all: 'All'
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PnlChartCard(t0) {
  const $ = _c(66);
  const {
    pubkey,
    registeredAt,
    hideTitle: t1
  } = t0;
  const hideTitle = t1 === undefined ? false : t1;
  const t = useTranslations("profile.stats");
  const locale = useLocale();
  const {
    formatBalance,
    formatD6,
    formatMarketCapAxis
  } = useFormattedCurrency();
  const availableRanges = availablePnlRanges(registeredAt);
  let t2;
  let t3;
  if ($[0] !== registeredAt) {
    t2 = () => {
      const registrationMs = Date.parse(registeredAt ?? "");
      if (!Number.isFinite(registrationMs) || registrationMs > Date.now()) {
        logger.warn("portfolio.pnl.registration_date_unavailable", {
          reason: registeredAt === null ? "missing" : "invalid_or_future"
        });
      }
    };
    t3 = [registeredAt];
    $[0] = registeredAt;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const [range, setRange] = useState(availableRanges.includes("1d") ? "1d" : "all");
  let t4;
  if ($[3] !== pubkey) {
    t4 = pubkey.length > 0 ? [pubkey] : [];
    $[3] = pubkey;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== pubkey || $[6] !== range || $[7] !== t4) {
    t5 = {
      pubkey,
      walletPubkeys: t4,
      range
    };
    $[5] = pubkey;
    $[6] = range;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      refetchInterval: false
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const pnlResult = useGetUserPnlChart(t5, t6);
  useRefetchWhenVisible(pnlResult);
  const isLoading = pnlResult.isPending;
  const isSuccess = pnlResult.isSuccess;
  const data = pnlResult.data;
  const [previous, setPrevious] = useState(null);
  let t7;
  let t8;
  if ($[10] !== data || $[11] !== isSuccess || $[12] !== range) {
    t7 = () => {
      if (isSuccess && data !== undefined) {
        setPrevious({
          range,
          performance: data.portfolioPerformance?.line ?? null,
          realized: data.realizedPnlLine,
          unrealizedEstimate: data.unrealizedPnlEstimateLine
        });
      }
    };
    t8 = [isSuccess, range, data];
    $[10] = data;
    $[11] = isSuccess;
    $[12] = range;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  const isFirstLoad = isLoading && previous === null;
  let presentation;
  let t9;
  if ($[15] !== data || $[16] !== formatBalance || $[17] !== formatD6 || $[18] !== isSuccess || $[19] !== previous || $[20] !== range || $[21] !== registeredAt || $[22] !== t) {
    presentation = buildPnlChartPresentation({
      range,
      data,
      isSuccess,
      previous,
      performanceLabel: t("pnl_performance"),
      realizedLabel: t("pnl_realized"),
      unrealizedEstimateLabel: t("pnl_unrealized_estimate"),
      formatBalance,
      formatD6
    });
    const nowMs = data !== undefined ? Date.parse(data.to) : presentation.series.reduce(_temp2, Number.NEGATIVE_INFINITY);
    const registrationMs_0 = Date.parse(registeredAt ?? "");
    const allStartMs = Number.isFinite(registrationMs_0) ? registrationMs_0 : presentation.series.reduce(_temp4, Number.POSITIVE_INFINITY);
    t9 = presentation.renderRange === "all" ? Math.max(1, (nowMs - (Number.isFinite(allStartMs) ? allStartMs : nowMs)) / 1000) : presentation.renderRange === "1m" ? 2592000 : presentation.renderRange === "1w" ? 604800 : 86400;
    $[15] = data;
    $[16] = formatBalance;
    $[17] = formatD6;
    $[18] = isSuccess;
    $[19] = previous;
    $[20] = range;
    $[21] = registeredAt;
    $[22] = t;
    $[23] = presentation;
    $[24] = t9;
  } else {
    presentation = $[23];
    t9 = $[24];
  }
  const windowSecs = t9;
  const T0 = Tabs;
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = value => setRange(value as PnlRange);
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t) {
    t11 = t("pnl_chart_range_label");
    $[26] = t;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const t12 = "shrink-0 gap-0";
  const T1 = TabsList;
  const t13 = "segmented";
  const t14 = "w-auto shrink-0 overflow-visible";
  const t15 = availableRanges.map(_temp5);
  let t16;
  if ($[28] !== T1 || $[29] !== t15) {
    t16 = <T1 variant={t13} className={t14}>{t15}</T1>;
    $[28] = T1;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== T0 || $[32] !== range || $[33] !== t10 || $[34] !== t11 || $[35] !== t16) {
    t17 = <T0 value={range} onValueChange={t10} aria-label={t11} className={t12}>{t16}</T0>;
    $[31] = T0;
    $[32] = range;
    $[33] = t10;
    $[34] = t11;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const timeframeTabs = t17;
  let t18;
  if ($[37] !== t) {
    t18 = t("pnl_chart_title");
    $[37] = t;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t18) {
    t19 = <span className="text-body-lg font-medium text-foreground-secondary">{t18}</span>;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t19 || $[42] !== timeframeTabs) {
    t20 = <div className="flex items-center justify-between gap-2">{t19}{timeframeTabs}</div>;
    $[41] = t19;
    $[42] = timeframeTabs;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const header = t20;
  let t21;
  if ($[44] !== formatBalance || $[45] !== formatMarketCapAxis) {
    t21 = (value_0, _fractionDigits, tickStep) => tickStep === undefined ? formatBalance(value_0, "compact") : formatMarketCapAxis(value_0, tickStep);
    $[44] = formatBalance;
    $[45] = formatMarketCapAxis;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== isFirstLoad || $[48] !== locale || $[49] !== presentation.formatValue || $[50] !== presentation.series || $[51] !== t21 || $[52] !== windowSecs) {
    t22 = <PnlChartCardLiveline series={presentation.series} loading={isFirstLoad} formatAxisValue={t21} formatTooltipValue={presentation.formatValue} locale={locale} windowSecs={windowSecs} />;
    $[47] = isFirstLoad;
    $[48] = locale;
    $[49] = presentation.formatValue;
    $[50] = presentation.series;
    $[51] = t21;
    $[52] = windowSecs;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  const chart = t22;
  if (hideTitle) {
    let t23;
    if ($[54] !== timeframeTabs) {
      t23 = <div className="flex justify-start">{timeframeTabs}</div>;
      $[54] = timeframeTabs;
      $[55] = t23;
    } else {
      t23 = $[55];
    }
    let t24;
    if ($[56] !== chart || $[57] !== t23) {
      t24 = <div className="flex flex-col gap-3">{chart}{t23}</div>;
      $[56] = chart;
      $[57] = t23;
      $[58] = t24;
    } else {
      t24 = $[58];
    }
    return t24;
  }
  let t23;
  if ($[59] !== header) {
    t23 = <PanelHeader className="pb-0">{header}</PanelHeader>;
    $[59] = header;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== chart) {
    t24 = <PanelContent className="pt-4 pb-0">{chart}</PanelContent>;
    $[61] = chart;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== t23 || $[64] !== t24) {
    t25 = <Panel className="gap-4">{t23}{t24}</Panel>;
    $[63] = t23;
    $[64] = t24;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  return t25;
}
function _temp5(r) {
  return <TabsTrigger key={r} value={r}>{RANGE_TAB_LABELS[r]}</TabsTrigger>;
}
function _temp4(earliest, entry_0) {
  return entry_0.points.reduce(_temp3, earliest);
}
function _temp3(first, point_0) {
  return Math.min(first, point_0.time * 1000);
}
function _temp2(latest, entry) {
  return entry.points.reduce(_temp, latest);
}
function _temp(last, point) {
  return Math.max(last, point.time * 1000);
}